import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import { format, startOfMonth, endOfMonth, addMonths, isSameDay } from "date-fns";
import { toast } from "react-toastify";

import CalendarMonthGrid from "../Components/CalendarMonthGrid";
import { formatSessionParticipantsLabel } from "../Components/sessionCalendarFormat";
import { schedulingApi, eventTypeColors, eventStatusColors } from "../Components/schedulingApi";

const theme = createTheme({
  palette: {
    primary: { main: "#5e72e4" },
    background: { default: "#f8f9fe" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 800, letterSpacing: "-1px" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
});

function parseUtcDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const hasTimezone = /[zZ]|[+-]\d{2}:\d{2}$/.test(raw);
  const normalized = hasTimezone ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export default function ScheduleCalendar() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [options, setOptions] = useState({ courses: [], lecturers: [], classrooms: [] });
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDrawerOpen, setDayDrawerOpen] = useState(false);
  const [isCalendarLoading, setIsCalendarLoading] = useState(true);
  const eventsRequestRef = useRef(0);

  const monthRange = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [calendarMonth]);

  const loadOptions = useCallback(async () => {
    try {
      const data = await schedulingApi.getOptions();
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      setOptions({
        courses: data.courses || [],
        lecturers: data.lecturers || [],
        classrooms: data.classrooms || [],
      });
    } catch (e) {
      toast.error(e?.message || "Failed to load options");
    }
  }, []);

  const loadEvents = useCallback(async (range) => {
    if (!range?.start || !range?.end) return;
    const requestId = ++eventsRequestRef.current;
    setIsCalendarLoading(true);
    try {
      const data = await schedulingApi.listEvents({ start: range.start, end: range.end });
      if (requestId !== eventsRequestRef.current) return;
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      // API returns start_datetime/end_datetime; calendar expects start/end
      const raw = data.events || [];
      const normalized = raw.map((ev) => ({
        ...ev,
        start: ev.start ?? ev.start_datetime,
        end: ev.end ?? ev.end_datetime,
      }));
      setEvents(normalized);
    } catch (e) {
      if (requestId !== eventsRequestRef.current) return;
      toast.error(e?.message || "Failed to load events");
    } finally {
      if (requestId === eventsRequestRef.current) {
        setIsCalendarLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadEvents(monthRange);
  }, [monthRange.start, monthRange.end, loadEvents]);

  const goMonth = useCallback((direction) => {
    setCalendarMonth((prev) => addMonths(prev, direction));
  }, []);

  const eventsForDay = useCallback(
    (day) => {
      return events.filter((ev) => {
        const start = parseUtcDate(ev.start) || (ev.start ? new Date(ev.start) : null);
        return start ? isSameDay(start, day) : false;
      });
    },
    [events]
  );

  const getEventColor = useCallback(
    (ev) =>
      ev.status === "pending_approval"
        ? eventStatusColors.pending_approval
        : eventTypeColors[ev.event_type] || eventTypeColors.class,
    []
  );

  const renderEvent = useCallback(
    (ev) => {
      const participantsLabel = formatSessionParticipantsLabel(ev);
      return (
        <Stack
          key={ev.id}
          spacing={0.15}
          sx={{
            fontSize: "0.7rem",
            bgcolor: alpha(getEventColor(ev), 0.25),
            color: "text.primary",
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
            borderLeft: `3px solid ${getEventColor(ev)}`,
            minWidth: 0,
            width: "100%",
          }}
        >
          <Typography variant="caption" noWrap sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
            {ev.title || "Event"}
            {ev.status === "pending_approval" ? " (Pending)" : ""}
          </Typography>
          {ev.unit_session_label ? (
            <Typography
              variant="caption"
              noWrap
              sx={{ fontSize: "0.65rem", lineHeight: 1.15, color: "text.secondary", fontWeight: 600 }}
            >
              {ev.unit_session_label}
            </Typography>
          ) : null}
          {participantsLabel ? (
            <Typography
              variant="caption"
              noWrap
              sx={{ fontSize: "0.62rem", lineHeight: 1.15, color: "text.secondary", fontWeight: 600 }}
            >
              {participantsLabel}
            </Typography>
          ) : null}
        </Stack>
      );
    },
    [getEventColor]
  );

  const onDayClick = useCallback((day) => {
    setSelectedDay(day);
    setDayDrawerOpen(true);
  }, []);

  const eventsOnSelectedDay = useMemo(
    () => (selectedDay ? eventsForDay(selectedDay) : []),
    [selectedDay, eventsForDay]
  );

  const openCreateForDay = useCallback(() => {
    if (!selectedDay) return;
    const start = new Date(selectedDay);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start);
    end.setHours(10, 0, 0, 0);
    setDayDrawerOpen(false);
    navigate("/schedule/events/create", {
      state: {
        initialValues: {
          start_datetime: start.toISOString().slice(0, 19),
          end_datetime: end.toISOString().slice(0, 19),
          duration_minutes: 60,
        },
      },
    });
  }, [selectedDay, navigate]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <Grid container spacing={0}>
          <Grid item xs={12} sx={{ p: { xs: 2, md: 3 } }}>
            <Container maxWidth="xl" disableGutters>
              <Stack spacing={3}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-1px", color: "var(--header-color, #111827)" }}>
                        Schedule
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, color: "var(--header-accent, #4c5fd6)" }}>
                        Full month view — click a day to see events or create one.
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate("/schedule/events/create")}
                      sx={{ borderRadius: 3 }}
                    >
                      Create Session
                    </Button>
                    <Button variant="outlined" startIcon={<MeetingRoomIcon />} onClick={() => navigate("/schedule/classrooms/create")} sx={{ borderRadius: 3 }}>
                      Create Classroom
                    </Button>
                  </Stack>
                </Box>

                <Paper
                  elevation={3}
                  sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 4,
                    overflow: "hidden",
                    background: (t) => `linear-gradient(145deg, ${t.palette.background.paper} 0%, ${alpha(t.palette.primary.main, 0.02)} 100%)`,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CalendarMonthGrid
                    calendarMonth={calendarMonth}
                    isLoading={isCalendarLoading}
                    onMonthChange={goMonth}
                    onDayClick={onDayClick}
                    eventsForDay={eventsForDay}
                    getEventColor={getEventColor}
                    renderEvent={renderEvent}
                    minDayHeight={100}
                  />
                </Paper>
              </Stack>
            </Container>
          </Grid>
        </Grid>
      </Box>

      <Drawer
        anchor="right"
        open={dayDrawerOpen}
        onClose={() => setDayDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 360 }, p: 2 } }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {selectedDay ? format(selectedDay, "EEEE, MMM d") : ""}
            </Typography>
            <IconButton onClick={() => setDayDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateForDay} fullWidth sx={{ borderRadius: 2 }}>
            Add session this day
          </Button>
          <Typography variant="subtitle2" color="text.secondary">
            Sessions
          </Typography>
          {eventsOnSelectedDay.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No sessions. Click &quot;Add session this day&quot; to create one.
            </Typography>
          ) : (
            <List dense disablePadding>
              {eventsOnSelectedDay.map((ev) => {
                const participantsLabel = formatSessionParticipantsLabel(ev);
                return (
                  <ListItemButton
                    key={ev.id}
                    onClick={() => {
                      setDayDrawerOpen(false);
                      navigate(`/schedule/events/${ev.event_id ?? ev.id}`);
                    }}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      borderLeft: `4px solid ${getEventColor(ev)}`,
                    }}
                  >
                    <ListItemText
                      primary={ev.title}
                      secondary={
                        <>
                          {ev.unit_session_label ? (
                            <>
                              <strong>{ev.unit_session_label}</strong>
                              {" \u2022 "}
                            </>
                          ) : null}
                          {ev.event_type} • {ev.start ? format((parseUtcDate(ev.start) || new Date(ev.start)), "HH:mm") : ""}{" "}
                          {ev.classroom?.room_code ? `• ${ev.classroom.room_code}` : ""}
                          {participantsLabel ? ` • ${participantsLabel}` : ""}
                        </>
                      }
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Stack>
      </Drawer>

    </ThemeProvider>
  );
}
